import { useState, useMemo, useEffect, useRef } from 'react';
import { ChevronDownIcon, SearchIcon } from '@heroicons/react/outline';
import classNames from 'classnames';
import { useTimezones } from '../hooks/useUIConstants';

interface TimezoneSelectorProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
}

// Fallback timezones in case API is not available
const FALLBACK_TIMEZONES = [
  { value: 'America/New_York', offset: 'UTC-5' },
  { value: 'America/Chicago', offset: 'UTC-6' },
  { value: 'America/Denver', offset: 'UTC-7' },
  { value: 'America/Los_Angeles', offset: 'UTC-8' },
];

const TimezoneSelector: React.FC<TimezoneSelectorProps> = ({
  value,
  onChange,
  disabled = false,
  placeholder,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  const { handPickedTimezones, allTimezones, loading, error } =
    useTimezones(searchQuery);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsExpanded(false);
      }
    };

    if (isExpanded) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isExpanded]);

  const formatTimezoneDisplay = (timezoneValue: string) => {
    return timezoneValue.replace(/\//g, ' / ').replace(/_/g, ' ');
  };

  const getTimezoneOffset = (timezoneValue: string) => {
    try {
      // Create a date object and format it to get the timezone offset
      const now = new Date();
      const utcTime = now.getTime() + now.getTimezoneOffset() * 60000;
      const targetTime = new Date(
        utcTime + new Date().getTimezoneOffset() * 60000,
      );

      // Use Intl.DateTimeFormat to get the offset
      const formatter = new Intl.DateTimeFormat('en', {
        timeZone: timezoneValue,
        timeZoneName: 'longOffset',
      });

      const parts = formatter.formatToParts(targetTime);
      const offsetPart = parts.find((part) => part.type === 'timeZoneName');

      if (offsetPart) {
        return offsetPart.value;
      }

      // Fallback: calculate offset manually with DST detection
      const currentDate = new Date();
      const isSummer =
        currentDate.getMonth() >= 3 && currentDate.getMonth() <= 9; // April to October

      // Use summer offset if we're in summer months, otherwise winter offset
      const testDate = isSummer ? new Date(2023, 6, 1) : new Date(2023, 0, 1);

      const offset = testDate.toLocaleString('en', {
        timeZone: timezoneValue,
        timeZoneName: 'longOffset',
      });

      return offset;
    } catch (error) {
      console.warn('Could not calculate timezone offset for:', timezoneValue);
      return 'Unknown';
    }
  };

  const getCurrentTimezone = () => {
    // First, try to find the timezone in all available timezones (both hand-picked and all)
    const allAvailableTimezones = [...handPickedTimezones, ...allTimezones];
    const timezone = allAvailableTimezones.find((tz) => tz.value === value);

    if (timezone) {
      return {
        ...timezone,
        displayValue: formatTimezoneDisplay(timezone.value),
        isPlaceholder: false as const,
      };
    }

    // If not found in API data, try to create a display for the saved value
    if (value) {
      const calculatedOffset = getTimezoneOffset(value);
      return {
        value: value,
        offset: calculatedOffset,
        displayValue: formatTimezoneDisplay(value),
        isPlaceholder: false as const,
      };
    }

    // Show placeholder when no value and placeholder prop is provided
    if (placeholder) {
      return {
        value: '',
        offset: '',
        displayValue: placeholder,
        isPlaceholder: true as const,
      };
    }

    // Fallback to hardcoded timezone if no value at all
    const fallbackTz = FALLBACK_TIMEZONES[0];
    return {
      ...fallbackTz,
      displayValue: formatTimezoneDisplay(fallbackTz.value),
      isPlaceholder: false as const,
    };
  };

  const displayTimezones = useMemo(() => {
    return searchQuery ? allTimezones : handPickedTimezones;
  }, [searchQuery, allTimezones, handPickedTimezones]);

  const currentTz = getCurrentTimezone();

  if (error) {
    console.error('Error loading timezones:', error);
  }

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Selected timezone display */}
      <div
        className={classNames(
          'flex items-center justify-between rounded border-2 border-transparent bg-neutral-50 py-3 px-4 transition-colors',
          'text-neutral-150 focus:border-secondary-50 focus:outline-none focus:ring-0',
          'disabled:bg-transparent disabled:text-caption disabled:font-medium disabled:text-green-150',
          !disabled &&
            'cursor-pointer hover:border-secondary-50 hover:bg-neutral-75',
        )}
        onClick={() => !disabled && setIsExpanded(!isExpanded)}
      >
        <div className="flex flex-col">
          <span
            className={classNames(
              'text-body',
              currentTz.isPlaceholder ? 'text-neutral-125' : 'text-neutral-150',
            )}
          >
            {currentTz.displayValue}
          </span>
          {!currentTz.isPlaceholder && currentTz.offset && (
            <span className="text-small-caption text-neutral-125">
              {currentTz.offset}
            </span>
          )}
        </div>
        {!disabled && (
          <ChevronDownIcon
            className={classNames(
              'h-4 w-4 text-neutral-125 transition-transform',
              isExpanded && 'rotate-180',
            )}
          />
        )}
      </div>

      {/* Expanded timezone list */}
      {isExpanded && !disabled && (
        <div className="absolute z-50 mt-1 w-full rounded-lg border border-neutral-75 bg-neutral-50 shadow-400">
          {/* Search field */}
          <div className="border-b border-neutral-50 bg-neutral-50 p-4">
            <div className="relative">
              <SearchIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-125" />
              <input
                type="text"
                placeholder="Search timezones..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full rounded border border-neutral-75 bg-white py-2 pl-10 pr-3 text-small-caption shadow-sm focus:border-secondary-50 focus:outline-none"
              />
            </div>
          </div>

          {/* Timezone list */}
          <div className="max-h-64 overflow-y-auto">
            {loading ? (
              <div className="p-4 text-center text-neutral-125">
                Loading timezones...
              </div>
            ) : displayTimezones.length > 0 ? (
              <div className="space-y-1 p-2">
                {/* Show section header only when not searching and we have hand-picked timezones */}
                {!searchQuery && handPickedTimezones.length > 0 && (
                  <div className="px-3 py-2">
                    <h4 className="font-sans text-small-caption text-neutral-125">
                      Popular Timezones
                    </h4>
                  </div>
                )}
                {displayTimezones.map((tz) => (
                  <button
                    key={tz.value}
                    onClick={() => {
                      onChange(tz.value);
                      setIsExpanded(false);
                    }}
                    className={classNames(
                      'flex w-full items-center justify-between rounded px-3 py-2 text-left text-small transition-colors',
                      'hover:bg-neutral-50',
                      value === tz.value && 'bg-secondary-25 text-green-150',
                    )}
                  >
                    <span className="text-body text-neutral-150">
                      {formatTimezoneDisplay(tz.value)}
                    </span>
                    <span className="text-small-caption text-neutral-125">
                      {tz.offset}
                    </span>
                  </button>
                ))}
              </div>
            ) : (
              <div className="p-4 text-center text-neutral-125">
                No timezones found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TimezoneSelector;
